import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { MemberOption, UserMapValue } from "../../models/User.ts";
import { MessageItem } from "../../models/LeftPanel.ts";
import store from "../../utils/store.ts";
import { createChatItem } from "../../utils/createDmChat.ts";
import Notification from "./Notification.tsx";
import "../../styles/NewChatCompose.css";

// Guardrail until collaborations support more than one assistant.
const ASSISTANT_LIMIT_MESSAGE = "Only one assistant can be added to a group.";

const countAgents = (members: MemberOption[]) =>
  members.filter((m) => m.userType === "agent").length;

interface CreatedResult {
  item: MessageItem;
  isExisting: boolean;
  chatType: "direct" | "group";
}

interface Props {
  mode: "dm" | "group";
  allUsersMap: Record<string, UserMapValue>;
  currentUser: any;
  onCreated: (result: CreatedResult) => void;
  onCancel: () => void;
  onSubmitting?: () => void;
}

const NewChatCompose: React.FC<Props> = ({
  mode,
  allUsersMap,
  currentUser,
  onCreated,
  onCancel,
  onSubmitting,
}) => {
  const theme = useTheme();
  const surface = theme.palette.surface;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<MemberOption[]>([]);
  const [selected, setSelected] = useState<MemberOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notif, setNotif] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });

  const hasAgent = countAgents(selected) > 0;

  const warnAssistantLimit = () =>
    setNotif({ open: true, message: ASSISTANT_LIMIT_MESSAGE, severity: "warning" });

  useEffect(() => {
    const allUsers = store.getState().allUsers;
    if (Array.isArray(allUsers)) {
      const currentUid = currentUser?.userId?.toLowerCase();
      setOptions(allUsers.filter((m) => m.uid?.toLowerCase() !== currentUid));
    }
  }, [currentUser]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onCancel();
  };

  const submit = async (members: MemberOption[]) => {
    if (members.length === 0 || isSubmitting) return;
    if (countAgents(members) > 1) {
      warnAssistantLimit();
      return;
    }
    onSubmitting?.();
    setIsSubmitting(true);
    try {
      const type = mode === "dm" || members.length === 1 ? "dm" : "group";
      const result = await createChatItem({
        type,
        members,
        currentUser: { userId: currentUser?.userId || "", name: currentUser?.name },
        allUsersMap,
      });
      onCreated(result);
    } catch (err) {
      console.error("Failed to create chat:", err);
      setIsSubmitting(false);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const filterOptions = (opts: MemberOption[], params: { inputValue: string }) =>
    opts.filter(
      (o) =>
        o.name?.toLowerCase().includes(params.inputValue.toLowerCase()) ||
        o.email?.toLowerCase().includes(params.inputValue.toLowerCase())
    );

  const getOptionLabel = (o: MemberOption) =>
    o.userType === "agent" && o.baseModel ? `${o.name} (${o.baseModel})` : o.name;

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: MemberOption) => {

    const blockedByLimit =
      hasAgent &&
      option.userType === "agent" &&
      !selected.some((s) => s.uid === option.uid);

    return (
      <li {...props} key={option.uid}>
        <div className="ndm-compose-option-row" style={blockedByLimit ? { opacity: 0.5 } : undefined}>
          <div
            className="ndm-compose-option-avatar"
            style={option.userType === "agent"
              ? { backgroundColor: `${surface?.inputBorderFocus}1F`, color: surface?.inputBorderFocus }
              : { backgroundColor: surface?.inputBorder, color: surface?.primaryText }
            }
          >
            {getInitials(option.name)}
          </div>
          <div className="ndm-compose-option-text">
            <span className="ndm-compose-option-name">{getOptionLabel(option)}</span>
            <span className="ndm-compose-option-email">
              {blockedByLimit ? ASSISTANT_LIMIT_MESSAGE : option.email}
            </span>
          </div>
        </div>
      </li>
    );
  };

  const paperProps = {
    sx: {
      borderRadius: "8px",
      boxShadow: "0px 0px 4px rgba(0,0,0,0.04), 8px 8px 16px rgba(0,0,0,0.08)",
      border: "1px solid #eceef0",
      mt: 1,
    },
  };

  const popperProps = {
    anchorEl: () => wrapperRef.current ?? document.body,
    style: { width: wrapperRef.current?.offsetWidth ?? 'auto' },
  };

  if (mode === "dm") {
    return (
      /* An inline compose bar pinned over the chat header, not a modal — it has
         no backdrop and does not cover the page, so it gets a labelled region
         rather than dialog semantics and a focus trap. Escape cancels. */
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape-to-cancel on the container is the correct place for it: it fires only while focus is inside the compose bar, which is exactly the desired scope. Removing the handler to satisfy the rule would remove a keyboard escape route.
      <div className="ndm-compose-overlay" role="region" aria-label="New direct message" onKeyDown={handleKeyDown}>
        <div className="ndm-compose-input-wrapper ndm-compose-input-wrapper--single" ref={wrapperRef} style={{ borderColor: surface?.inputBorderFocus }}>
          <span className="ndm-compose-to-label">To:</span>
          <Autocomplete<MemberOption>
            className="ndm-compose-autocomplete"
            options={options}
            onChange={(_, value) => { if (value) submit([value]); }}
            disabled={isSubmitting}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(o, v) => o.uid === v.uid}
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder="Search by name or email..."
                variant="standard"
                // The "To:" text beside the field is decorative markup, so the
                // input needs its own name.
                inputProps={{ ...params.inputProps, 'aria-label': 'To' }}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  style: { fontSize: 20, fontFamily: "var(--app-font-family, 'Proxima Nova')" },
                  endAdornment: isSubmitting ? <CircularProgress size={18} /> : null,
                }}
              />
            )}
            slotProps={{ paper: paperProps, listbox: { sx: { p: 0 } }, popper: popperProps }}
            sx={{
              "& .MuiAutocomplete-popupIndicator": { display: "none" },
              "& .MuiAutocomplete-clearIndicator": { display: "none" },
            }}
          />
          <IconButton
            className="ndm-compose-close-btn"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  // group mode — multi-select with chips + create button
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- see the dm-mode branch above: Escape-to-cancel belongs on the container.
    <div className="ndm-compose-overlay" role="region" aria-label="New group chat" onKeyDown={handleKeyDown}>
      <div className="ndm-compose-input-wrapper" ref={wrapperRef} style={{ borderColor: surface?.inputBorderFocus }}>
        <span className="ndm-compose-to-label">To:</span>
        <Autocomplete<MemberOption, true>
          multiple
          className="ndm-compose-autocomplete"
          options={options}
          value={selected}
          onChange={(_, value) => {
            if (countAgents(value) > 1) {
              warnAssistantLimit();
              return;
            }
            setSelected(value);
          }}
          disabled={isSubmitting}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(o, v) => o.uid === v.uid}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagProps = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option.uid}
                  label={option.name}
                  size="small"
                  sx={{
                    fontFamily: "var(--app-font-family, 'Proxima Nova')",
                    fontSize: 13,
                    borderRadius: "6px",
                    backgroundColor: `${surface?.inputBorderFocus}1F`,
                    color: surface?.inputBorderFocus,
                    "& .MuiChip-deleteIcon": { color: surface?.inputBorderFocus, opacity: 0.6 },
                  }}
                />
              );
            })
          }
          renderOption={renderOption}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder={selected.length === 0 ? "Search by name or email..." : ""}
              variant="standard"
              onKeyDown={(e) => {
                if (e.key === "Enter" && selected.length > 0 && !isSubmitting) {
                  e.preventDefault();
                  submit(selected);
                }
              }}
              inputProps={{ ...params.inputProps, 'aria-label': 'To' }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                style: { fontSize: 16, fontFamily: "var(--app-font-family, 'Proxima Nova')" },
              }}
            />
          )}
          slotProps={{ paper: paperProps, listbox: { sx: { p: 0 } }, popper: popperProps }}
          sx={{
            "& .MuiAutocomplete-popupIndicator": { display: "none" },
            "& .MuiAutocomplete-clearIndicator": { display: "none" },
          }}
        />
        {selected.length > 0 && (
          isSubmitting ? (
            <CircularProgress size={18} sx={{ mx: 1, flexShrink: 0 }} />
          ) : (
            <Button
              className="ndm-compose-create-btn"
              size="small"
              variant="contained"
              disableElevation
              onClick={() => submit(selected)}
              sx={{
                fontFamily: "var(--app-font-family, 'Proxima Nova')",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 0.5,
                flexShrink: 0,
                backgroundColor: surface?.inputBorderFocus,
                "&:hover": { backgroundColor: surface?.inputBorderFocus, filter: "brightness(0.9)" },
              }}
            >
              Create group
            </Button>
          )
        )}
        <IconButton
          className="ndm-compose-close-btn"
          size="small"
          onClick={onCancel}
          disabled={isSubmitting}
          aria-label="Cancel"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <Notification
        {...notif}
        onClose={() => setNotif({ ...notif, open: false })}
      />
    </div>
  );
};

export default NewChatCompose;
